import { LoadingOverlay, Stack } from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { showNotification } from "@mantine/notifications"
import { ErrorBox, DEFAULT_PAGE_SIZE } from "@tfg/ui"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import classes from "~/assets/css/Certificates.module.css"
import { queryClient } from "~/backend/queryClient"
import {
  CertificateByOrganizationDto,
  findCertificatesByOrganizationIdQueryKey,
  UpdateCertificateRequestDto,
  useFindCertificateTypesByOrganizationId,
  useUpdateCertificate,
} from "~/gen/api"
import { RenderField } from "~/pages/Admin/components/RenderField"
import { FormFieldConfig } from "~/pages/Admin/components/TypesForm"
import { CertificateFormSubmitButton } from "~/pages/Brand/Certificates/components/CertificateFormSubmitButton"
import { CertificateProductionSite } from "~/pages/Brand/Certificates/components/CertificateProductionSite"
import {
  FilteredCertificate,
  getCertificateTemplate,
  getTemplateKeysForForm,
  getTypeOfKey,
} from "~/pages/Brand/Certificates/components/templates.ts"
import {
  getScoreTranslations,
  getTooltipTranslation,
  labelHolderTranslations,
  placeHolderTranslations,
} from "~/pages/Brand/Certificates/type.ts"
import { showAxiosError } from "~/utils/showAxiosError"

type Props = {
  certificate: CertificateByOrganizationDto
}

export function CertificateForm({ certificate }: Props) {
  const { t } = useTranslation("certificates")

  const { data } = useFindCertificateTypesByOrganizationId()
  const certificateTypes = data?.data

  const {
    mutateAsync: updateCertificate,
    isPending: isLoading,
    error,
  } = useUpdateCertificate({
    mutation: {
      onSuccess: async () => {
        showNotification({
          color: "green",
          message: t("modal.success"),
        })
        await queryClient.invalidateQueries({
          queryKey: findCertificatesByOrganizationIdQueryKey({
            pagination: {
              pageIndex: 0,
              pageSize: DEFAULT_PAGE_SIZE,
            },
          }),
        })
      },
      onError: (error) => {
        showAxiosError({
          error,
          showNotification: true,
        })
      },
    },
  })

  const initialValues = useMemo(
    () => ({
      ...certificate,
      reference: certificate.reference ?? "",
      name: certificate.name ?? certificate.type.name,
      typeId: certificate.type.id,
      productionSiteId: certificate.productionSite.id,
    }),
    [certificate]
  )
  const selectOptionsMap = useMemo(() => {
    return {
      typeId: certificateTypes?.map((type) => ({
        label: type.name,
        value: type.id,
      })),
      score: Object.entries(getScoreTranslations(t)).map(([score, label]) => ({
        label,
        value: score,
      })),
    }
  }, [certificateTypes, t])

  const getSelectOptions = useCallback(
    (key: string) => {
      if (key in selectOptionsMap) {
        return selectOptionsMap[key as keyof typeof selectOptionsMap]
      }
      return []
    },
    [selectOptionsMap]
  )

  const { fields, template } = useMemo(() => {
    const template = getCertificateTemplate(initialValues.type.category)
    const keys = getTemplateKeysForForm(template)
    const formFields: FormFieldConfig[] = keys.map((key) => {
      const type = getTypeOfKey(key)

      return {
        type,
        name: key,
        label: t(labelHolderTranslations[key as keyof FilteredCertificate]),
        placeholder: t(
          placeHolderTranslations[key as keyof FilteredCertificate]
        ),
        customClass: classes,
        options: getSelectOptions(key),
        tooltip: getTooltipTranslation(key, t),
      }
    })
    return { fields: formFields, template }
  }, [initialValues, t, getSelectOptions])

  const form = useForm<UpdateCertificateRequestDto>({
    validate: zodResolver(template),
    initialValues,
  })

  const submit = async () => {
    form.setFieldValue("status", "PROCESSED")
    await updateCertificate({ id: certificate.id, data: form.getValues() })
  }

  return (
    <>
      <form
        onSubmit={form.onSubmit((values) =>
          updateCertificate({ id: certificate.id, data: values })
        )}
        style={{
          height: "fit-content",
        }}
      >
        <LoadingOverlay visible={isLoading} />
        <ErrorBox error={error} />
        <Stack justify="flex-start" gap={0} h="100%">
          {/*OPTIONAL FIELDS HERE*/}
          <div
            style={{
              flex: "1 1 90%",
              display: "flex",
              height: "100%",
              flexDirection: "column",
            }}
          >
            {certificate.productionSite && (
              <CertificateProductionSite certificate={certificate} />
            )}
            {fields.map((field) => (
              <div key={field.name}>{RenderField({ field, form })}</div>
            ))}
          </div>
          <div
            style={{
              flex: "0 1 10%",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CertificateFormSubmitButton
              form={form}
              submit={submit}
              isLoading={isLoading}
            />
          </div>
        </Stack>
      </form>
    </>
  )
}
